import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import AttensionSpanIcon from './Icons/AttensionSpanIcon.svg';
import engagementImage from './Icons/engagementIcon.svg';
import emotionalStateIcon from './Icons/emotionalStateIcon.svg';

import { handleError } from '../../APIs/apiUtils';
import { getApiResponse } from '../../APIs/getApiResponse';

import DatePicker from './customDataPicker';
import BehaviourScoreChart from '../Behaviour/Charts/BehaviourScoreChart';
import AttentioSpanLineChart from '../StudentBehaviour/CustomAttentionSpan';
import { CustomEngagementRateChart } from './EngagementRateChart';
import { EmotionChart } from '../Behaviour/Charts/EmotionChart';
import EmotionalStateChart from '../Behaviour/Charts/EmotionSatechart';

const StatCard = ({ title, value, img: Image, isLoading }) => {
    const displayValue = isLoading ? (
        <div className="flex items-center justify-center h-6">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
    ) : (
        value
    );

    const ImageIcons = Image ? (
        <img src={Image} alt={`${title} icon`} className="w-8 h-8" />
    ) : (
        null
    );

    return (
        <div className="flex items-center justify-between p-3 bg-white border rounded-xl w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.75rem)] xl:w-64 font-[Archivo]">
            <div className="space-y-0">
                <p className="text-xs md:text-sm text-gray-600 mb-0.5">{title}</p>
                <p className="text-xl md:text-2xl font-bold text-black-800 leading-tight">
                    {displayValue}
                </p>
            </div>
            <div className="p-2 rounded-full">
                {ImageIcons}
            </div>
        </div>
    );
}; 

const SubjectNavigationCard = React.forwardRef(({ subjectName, isActive, onClick, isLoading }, ref) => {
    return (
        <button
            onClick={onClick}
            className={`px-4 py-2 text-sm font-semibold relative transition-all duration-200 ease-in-out hover:scale-110 transform flex-shrink-0 font-[Archivo]
                ${isActive
                    ? 'text-[#2573EB] after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-1 after:w-8 after:bg-[#2573EB] after:rounded-t-sm' // Changed h-0.5 to h-1 and w-10 to w-8
                    : 'text-gray-700 hover:text-[#2573EB]'
                }
                ${isLoading ? 'animate-pulse' : ''}
            `}
            disabled={isLoading}
            ref={ref}
        >
            {isLoading ? <div className="h-4 bg-gray-300 rounded w-20"></div> : subjectName}
        </button>
    );
});

const ChartCard = React.memo(({ title, children, customHeight = "h-80 sm:h-150 md:h-100", buttons }) => {
    return (
        <div className="p-4 md:p-6 bg-white border rounded-xl h-full font-[Archivo]">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                <h3 className="text-lg md:text-xl font-semibold">{title}</h3>
                {buttons}
            </div>
            <div className={`relative ${customHeight}`}>
                {children}
            </div>
        </div>
    );
});

const LOCAL_STORAGE_START_DATE_KEY = 'global_start_date';
const LOCAL_STORAGE_END_DATE_KEY = 'global_end_date';

const SubjectAnalyticsBody = () => {
    const { id: student_Id, name: student_Name_from_url, subject: active_subject_from_url } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const school_name = localStorage.getItem('school_name') || 'default_school';

    const [startDate, setStartDate] = useState(() => {
        return localStorage.getItem(LOCAL_STORAGE_START_DATE_KEY) || null;
    });
    const [endDate, setEndDate] = useState(() => {
        return localStorage.getItem(LOCAL_STORAGE_END_DATE_KEY) || null;
    });

    const [isLoading, setIsLoading] = useState(true);
    const [apiData, setApiData] = useState({
        subjectsData: null,
        weeklyReportData: null,
        weeklyBehaviourData: null,
        subjectEmotionsData: null,
    });
    const scrollContainerRef = useRef(null);
    const activeSubjectRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const [activeTab, setActiveTab] = useState('Attention Span');
    const tabLabels = ['Attention Span', 'Engagement Rate'];

    useEffect(() => {
        if (startDate) {
            localStorage.setItem(LOCAL_STORAGE_START_DATE_KEY, startDate);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
        }
    }, [startDate]);

    useEffect(() => {
        if (endDate) {
            localStorage.setItem(LOCAL_STORAGE_END_DATE_KEY, endDate);
        } else {
            localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);
        }
    }, [endDate]);

    const formatDate = (dateString, mode = 'date') => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            const parts = dateString.split('T')[0].split('-');
            if (parts.length === 3) {
                return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateString;
        }

        if (mode === 'date') {
            return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } else if (mode === 'time') {
            return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
        } else if (mode === 'time24') {
            return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
        }
        return dateString;
    };


    useEffect(() => {
        if (!student_Id || !school_name) return;

        const fetchAllData = async () => {
            setIsLoading(true);
            let dateParams = '';
            if (startDate && endDate) {
                const dateObjectStart = new Date(startDate);
                const dateObjectEnd = new Date(endDate);
                if (!isNaN(dateObjectStart.getTime()) && !isNaN(dateObjectEnd.getTime())) {
                    const formattedStartDate = dateObjectStart.toISOString().split('T')[0];
                    const formattedEndDate = dateObjectEnd.toISOString().split('T')[0];
                    dateParams = `&start_date=${formattedStartDate}&end_date=${formattedEndDate}`;
                } else {
                    toast.error("Invalid date format. Please select the dates again.");
                }
            }

            const schoolParam = `school=${school_name}`;
            const endpoints = {
                subjectsData: `/api/students/report/${student_Id}/?${schoolParam}${dateParams}`,
                weeklyReportData: `/api/subject-weekly-report/${student_Id}/?${schoolParam}${dateParams}`,
                weeklyBehaviourData: `/api/subject-weekly-behaviour/${student_Id}/?${schoolParam}${dateParams}`,
                subjectEmotionsData: `/api/subject-wise-emotions/${student_Id}/?${schoolParam}${dateParams}`,
            };

            try {
                const [subjectsResponse, weeklyReportResponse, weeklyBehaviourResponse, emotionsResponse] = await Promise.all([
                    getApiResponse(endpoints.subjectsData),
                    getApiResponse(endpoints.weeklyReportData),
                    getApiResponse(endpoints.weeklyBehaviourData),
                    getApiResponse(endpoints.subjectEmotionsData),
                ]);
                setApiData({ subjectsData: subjectsResponse, weeklyReportData: weeklyReportResponse, weeklyBehaviourData: weeklyBehaviourResponse, subjectEmotionsData: emotionsResponse });
            } catch (error) {
                setApiData({ subjectsData: null, weeklyReportData: null, weeklyBehaviourData: null, subjectEmotionsData: null });
                const { shouldRedirect } = handleError(error);
                if (shouldRedirect) {
                    setTimeout(() => {
                        localStorage.clear();
                        window.location.href = '/login';
                    }, 3000);
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllData();
    }, [student_Id, school_name, startDate, endDate]);
    console.log("API Data:", apiData.weeklyReportData);

    const subjectsList = useMemo(() => apiData.subjectsData?.data?.subjects || [], [apiData.subjectsData]);

    const currentSubjectData = useMemo(() => {
        return subjectsList.find(
            (sub) => sub.subject.toLowerCase() === active_subject_from_url.toLowerCase()
        ) || null;
    }, [active_subject_from_url, subjectsList]);

    const {
        attentionChartData,
        engagementChartData,
        maxAttentionSpanValue,
        attentionXLabels,
        attentionTooltipLabels,
        } = useMemo(() => {
        const defaultState = {
            attentionChartData: [],
            engagementChartData: [],
            maxAttentionSpanValue: 0,
            attentionXLabels: [],
            attentionTooltipLabels: [],
        };

        const allSubjectsReportData = apiData.weeklyReportData?.data?.data;
        if (!allSubjectsReportData) return defaultState;

        const subjectReport = allSubjectsReportData[active_subject_from_url];
        if (!subjectReport) return defaultState;

        const attentionData = [];
        const engagementData = [];
        const xLabels = [];
        const tooltipLabels = [];

        // ✅ Handle direct chart_data (your current API format)
        if (subjectReport.chart_data) {
            const { labels, datasets } = subjectReport.chart_data;

            const attentionDataset = datasets.find(ds =>
            ds.label.toLowerCase().includes("attention")
            );
            const engagementDataset = datasets.find(ds =>
            ds.label.toLowerCase().includes("engagement")
            );

            if (attentionDataset) attentionData.push(...attentionDataset.data);
            if (engagementDataset) engagementData.push(...engagementDataset.data);

            labels.forEach((label) => {
            const m = label.match(
                /^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2})-(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2})$/
            );

            if (m) {
                const [_, startDatePart, startTimePart, endDatePart, endTimePart] = m;
                if (startDatePart === endDatePart) {
                xLabels.push(`${formatDate(startDatePart, "date")} [${startTimePart} - ${endTimePart}]`);
                tooltipLabels.push(`${formatDate(startDatePart, "date")} ${startTimePart}-${endTimePart}`);
                } else {
                xLabels.push(`${formatDate(startDatePart, "date")} - ${formatDate(endDatePart, "date")}`);
                tooltipLabels.push(
                    `${formatDate(startDatePart, "date")} ${startTimePart} - ${formatDate(endDatePart, "date")} ${endTimePart}`
                );
                }
            } else {
                xLabels.push(label);
                tooltipLabels.push(label);
            }
            });
        }

        // ✅ If your API in future sends analyzed_periods_details, keep old handling too
        if (subjectReport.analyzed_periods_details) {
            subjectReport.analyzed_periods_details.forEach((period) => {
            const chartData = period.chart_data;
            if (chartData?.labels?.length > 0) {
                const attentionDataset = chartData.datasets.find(ds =>
                ds.label.toLowerCase().includes("attention")
                );
                const engagementDataset = chartData.datasets.find(ds =>
                ds.label.toLowerCase().includes("engagement")
                );

                if (attentionDataset) attentionData.push(...attentionDataset.data);
                if (engagementDataset) engagementData.push(...engagementDataset.data);

                chartData.labels.forEach((label) => {
                xLabels.push(label);
                tooltipLabels.push(label);
                });
            }
            });
        }

        // ✅ calculate max attention from all subjects
        let allAttentionValues = [];
        Object.values(allSubjectsReportData).forEach((subjectData) => {
            const datasets = subjectData.chart_data?.datasets || [];
            const attDS = datasets.find(ds => ds.label.toLowerCase().includes("attention"));
            if (attDS) allAttentionValues.push(...attDS.data);
        });

        return {
            attentionChartData: attentionData,
            engagementChartData: engagementData,
            maxAttentionSpanValue: Math.max(...allAttentionValues, 0),
            attentionXLabels: xLabels,
            attentionTooltipLabels: tooltipLabels,
        };
        }, [apiData.weeklyReportData, active_subject_from_url, formatDate]);


    const allSubjectsBehaviourData = useMemo(() => {
        const allSubjectReports = apiData.weeklyBehaviourData?.data;
        if (!allSubjectReports) {
            return { chartData: [], subjects: [], analysisRange: null };
        }

        const labels = allSubjectReports.labels || [];
        const datasets = allSubjectReports.datasets || [];

        const subjects = datasets.map(ds => ds.label);

        const chartData = labels.map((label, idx) => {
            const record = { name: label };
            datasets.forEach(ds => {
                record[ds.label] = ds.data[idx] ?? 0;
            });
            return record;
        });

        const analysisRange = allSubjectReports.behaviour_analysis_range
            ? {
                start: allSubjectReports.behaviour_analysis_range.start,
                end: allSubjectReports.behaviour_analysis_range.end,
            }
            : null;


        return {
            chartData,
            subjects,
            analysisRange,
        };
    }, [apiData.weeklyBehaviourData]);


    const emotionChartDisplayData = useMemo(() => {
        const emotionsForSubject = apiData.subjectEmotionsData?.data?.data?.[active_subject_from_url];
        if (!emotionsForSubject) return null;

        return {
            happy: emotionsForSubject.happy || 0,
            calm: emotionsForSubject.neutral || 0,
            joy: emotionsForSubject.surprise || 0,
            'very sad': emotionsForSubject.angry || 0,
            sad: emotionsForSubject.sad || 0,
        };
    }, [apiData.subjectEmotionsData, active_subject_from_url]);

    const headerInfo = useMemo(() => {
        const data = apiData.subjectsData?.data;
        if (!data) {
            return { name: student_Name_from_url, classroom: '', dateRange: '' };
        }

        let dateRangeStr = '';
        if (data.meta?.range_start && data.meta?.range_end && !startDate) {
            const formattedStart = formatDate(data.meta.range_start);
            const formattedEnd = formatDate(data.meta.range_end);
            dateRangeStr = `${formattedStart} to ${formattedEnd}`;
        }

        return {
            name: data.student_name || student_Name_from_url,
            classroom: data.classroom || '',
            dateRange: dateRangeStr
        };
    }, [apiData.subjectsData, student_Name_from_url, startDate, formatDate]);

    useEffect(() => {
        if (isLoading || subjectsList.length === 0) {
            return;
        }

        const isActiveSubjectPresent = subjectsList.some(
            (sub) => sub.subject.toLowerCase() === active_subject_from_url.toLowerCase()
        );

        if (!isActiveSubjectPresent) {
            const newActiveSubject = subjectsList[0].subject;

            navigate(
                `/SubjectPerformance/${student_Id}/${headerInfo.name}/${newActiveSubject}`,
                {
                    replace: true,
                    state: {
                        showRedirectToast: true,
                        previousSubject: active_subject_from_url
                    }
                }
            );
        }
    }, [subjectsList, active_subject_from_url, isLoading, navigate, student_Id, headerInfo.name]);

    useEffect(() => {
        if (location.state?.showRedirectToast) {
            const { previousSubject } = location.state;

            toast.info(`"${previousSubject}" was unavailable for these dates. Now showing "${active_subject_from_url}".`, {
                autoClose: 4000,
            });

            setTimeout(() => {
                const { state, ...rest } = location;
                navigate(rest, { replace: true });
            }, 50);
        }
    }, [location, navigate, active_subject_from_url]);

    const handleSubjectChange = (newSubjectName) => {
        if (newSubjectName.toLowerCase() !== active_subject_from_url.toLowerCase()) {
            navigate(`/SubjectPerformance/${student_Id}/${headerInfo.name}/${newSubjectName}`);
        }
    };

    const formatAttentionSpan = (minutes) => {
        if (minutes === null || minutes === undefined || isNaN(minutes)) return "N/A";
        if (minutes < 1) {
            const seconds = Math.round(minutes * 60);
            return (
                <>
                    <span>{seconds}</span>
                    <span className="text-sm font-semibold ml-1 mr-2 font-[Archivo]">sec/class</span>
                </>
            );
        }
        const integer_minutes = Math.floor(minutes);
        return (
            <>
                <span>{integer_minutes}</span>
                <span className="text-sm font-semibold ml-1 mr-2 font-[Archivo]">min/class</span>
            </>
        );
    };

    const checkScrollability = useCallback(() => {
        const container = scrollContainerRef.current;
        if (container) {
            const { scrollLeft, scrollWidth, clientWidth } = container;
            const scrollToEnd = scrollWidth - clientWidth;
            setCanScrollLeft(scrollLeft > 0);
            setCanScrollRight(Math.ceil(scrollLeft) < scrollToEnd);
        }
    }, []);

    const scrollToActiveSubject = useCallback(() => {
        if (activeSubjectRef.current && scrollContainerRef.current) {
            const container = scrollContainerRef.current;
            const activeElement = activeSubjectRef.current;
            const containerRect = container.getBoundingClientRect();
            const activeElementRect = activeElement.getBoundingClientRect();

            const isOutOfView = activeElementRect.left < containerRect.left || activeElementRect.right > containerRect.right;
            if (isOutOfView) {
                const scrollLeftPosition = activeElement.offsetLeft - container.offsetWidth / 2 + activeElement.offsetWidth / 2;
                container.scrollTo({
                    left: scrollLeftPosition,
                    behavior: 'smooth'
                });
            }
        }
    }, []);

    useEffect(() => {
        const container = scrollContainerRef.current;
        if (container) {
            checkScrollability();
            container.addEventListener('scroll', checkScrollability);
            window.addEventListener('resize', checkScrollability);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScrollability);
                window.removeEventListener('resize', checkScrollability);
            }
        };
    }, [checkScrollability, subjectsList]);

    useEffect(() => {
        if (subjectsList.length > 0) {
            scrollToActiveSubject();
        }
    }, [subjectsList, active_subject_from_url, scrollToActiveSubject]);

    const handleScroll = (direction) => {
        scrollContainerRef.current?.scrollBy({
            left: direction === 'left' ? -300 : 300,
            behavior: 'smooth'
        });
    };

    const chartTitle = activeTab === 'Attention Span' ? 'Attention Span' : 'Engagement Rate';

    const chartButtons = (
        <div className="flex gap-4 sm:gap-3 rounded-3xl bg-[#F8F8F8] px-2 sm:px-5 py-1">
            {tabLabels.map((tab) => (
                <button
                    key={tab}
                    className={`px-6 sm:px-6 py-2 text-xs sm:text-sm rounded-full ${activeTab === tab ? "bg-white font-semibold" : "text-gray-500"
                        } font-[Archivo]`}
                    onClick={() => setActiveTab(tab)}
                >
                    {tab}
                </button>
            ))}
        </div>
    );

    const handleNavigateToDashboard = () => {
        localStorage.removeItem(LOCAL_STORAGE_START_DATE_KEY);
        localStorage.removeItem(LOCAL_STORAGE_END_DATE_KEY);
        setStartDate(null);
        setEndDate(null);
        navigate("/");
    };

    return (
        <div className="flex flex-col h-full p-2 md:p-4 font-[Archivo]">
            <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1 font-[Archivo]">
                <div>
                    <div className="flex items-center text-sm text-black-800 flex-wrap font-[Archivo]">
                        <span
                            className="pr-2 cursor-pointer hover:text-blue-600"
                            onClick={() => handleNavigateToDashboard()}
                        >
                            Behaviour Dashboard
                        </span>
                        <span className="pr-2">&gt;</span>
                        <span
                            className="pr-2 cursor-pointer hover:text-blue-600"
                            onClick={() =>
                                navigate(`/StudentPerformance/${student_Id}/${headerInfo.name}`)
                            }
                        >
                            {headerInfo.name} Profile
                        </span>
                        <span className="hidden sm:inline text-blue-600">
                            &gt; {active_subject_from_url}
                        </span>
                    </div>
                    <h1 className="text-lg sm:text-xl text-[#000000] font-[500] leading-tight mt-0.5">
                        {active_subject_from_url} Performance
                    </h1>
                    <div className="mt-0.5 text-xs text-gray-600">
                        <h6>
                            Student:{" "}
                            <span className="font-semibold text-black">
                                {headerInfo.name}
                            </span>{" "}
                            | Classroom:{" "}
                            <span className="font-semibold text-black">
                                {headerInfo.classroom}
                            </span>
                        </h6>
                    </div>
                </div>
                <DatePicker
                    onDateSelect={(start, end) => {
                        setStartDate(start);
                        setEndDate(end);
                    }}
                    initialStartDate={startDate}
                    initialEndDate={endDate}
                />
            </header>

            <nav className="relative mb-4">
                <div
                    ref={scrollContainerRef}
                    className="flex overflow-x-auto whitespace-nowrap scrollbar-hide space-x-1 pb-1"
                >
                    {isLoading ? (
                        <>
                            <SubjectNavigationCard isLoading={true} />
                            <SubjectNavigationCard isLoading={true} />
                            <SubjectNavigationCard isLoading={true} />
                            <SubjectNavigationCard isLoading={true} />
                        </>
                    ) : subjectsList.length > 0 ? (
                        subjectsList.map((sub) => (
                            <SubjectNavigationCard
                                key={sub.subject}
                                subjectName={sub.subject}
                                isActive={
                                    sub.subject.toLowerCase() ===
                                    active_subject_from_url.toLowerCase()
                                }
                                onClick={() => handleSubjectChange(sub.subject)}
                                ref={
                                    sub.subject.toLowerCase() ===
                                        active_subject_from_url.toLowerCase()
                                        ? activeSubjectRef
                                        : null
                                }
                            />
                        ))
                    ) : (
                        <p className="text-gray-500 text-sm py-1 font-[Archivo]">
                            No subjects available for the selected period.
                        </p>
                    )}
                </div>
                {canScrollLeft && (
                    <button
                        onClick={() => handleScroll("left")}
                        className="absolute left-0 top-1/2 -translate-y-1/2 bg-gray-200/80 rounded-full p-2 shadow-md hover:bg-gray-100 z-10"
                        aria-label="Scroll subjects left"
                    >
                        <FaChevronLeft size={14} />
                    </button>
                )}
                {canScrollRight && (
                    <button
                        onClick={() => handleScroll("right")}
                        className="absolute right-0 top-1/2 -translate-y-1/2 bg-gray-200/80 rounded-full p-2 shadow-md hover:bg-gray-100 z-10"
                        aria-label="Scroll subjects right"
                    >
                        <FaChevronRight size={14} />
                    </button>
                )}
                <div className="mt-1 border-b border-gray-300"></div>
            </nav>


            <main className="flex-1 flex flex-col gap-4">
                <section className="flex flex-wrap gap-4">
                    <StatCard
                        title="Avg Attention Span"
                        value={isLoading ? null : formatAttentionSpan(currentSubjectData?.attention_minutes_per_period)}
                        img={AttensionSpanIcon}
                        isLoading={isLoading}
                    />
                    <StatCard
                        title="Attendance Rate"
                        value={
                            isLoading ? null : (
                                (currentSubjectData?.attendance !== null && currentSubjectData?.attendance !== undefined) ? (
                                    <>
                                        {`${(currentSubjectData.attendance)}`}
                                        <span className="text-sm font-semibold ml-0.5 font-[Archivo]">%</span>
                                    </>
                                ) : <span className="font-[Archivo]">N/A</span>
                            )
                        }
                        img={emotionalStateIcon}
                        isLoading={isLoading}
                    />
                    <StatCard
                        title="Avg Engagement Rate"
                        value={
                            isLoading ? null : (
                                (currentSubjectData?.average_engagement !== null && currentSubjectData?.average_engagement !== undefined) ? (
                                    <>
                                        {`${(currentSubjectData.average_engagement)}`}
                                        <span className="text-sm font-semibold ml-0.5 font-[Archivo]">%</span>
                                    </>
                                ) : <span className="font-[Archivo]">N/A</span>
                            )
                        }
                        img={engagementImage}
                        isLoading={isLoading}
                    />
                </section>

                <section className="flex flex-col lg:flex-row gap-4">
                    <div className="flex-1 flex flex-col gap-4">
                        <ChartCard title={chartTitle} buttons={chartButtons}>
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full text-gray-500 font-[Archivo]">Loading chart data...</div>
                            ) : activeTab === 'Attention Span' ? (
                                <AttentioSpanLineChart
                                    chartData={attentionChartData}
                                    attentionIntervalDuration={1}
                                    maxAttentionValue={maxAttentionSpanValue}
                                    xLabels={attentionXLabels}
                                    tooltipLabels={attentionTooltipLabels}
                                    xAxisTitle={"Period Time Intervals"}
                                />
                            ) : (
                                <CustomEngagementRateChart
                                    engagementData={engagementChartData}
                                    engage_interval_duration={1}
                                    xLabels={attentionXLabels}
                                    xAxisTitle={"Period Time Intervals"}
                                    tooltipLabels={attentionTooltipLabels}
                                />
                                
                            )}
                        </ChartCard>
                    </div>

                    <div className="w-full lg:w-1/3">
                        <ChartCard title="Emotional State">
                            {isLoading ? (
                                <div className="flex items-center justify-center h-full text-gray-500 font-[Archivo]">Loading emotional data...</div>
                            ) : emotionChartDisplayData ? (
                                <EmotionalStateChart emotionData={emotionChartDisplayData} />
                                
                            ) : (
                                <div className="flex items-center justify-center h-full text-gray-400 font-[Archivo]">No emotional data available.</div>
                            )}
                        </ChartCard>
                    </div>
                </section>
                <section className="w-full min-h-[35rem] flex flex-col border rounded-xl p-4">
                    {isLoading ? (
                        <div className="flex-grow flex items-center justify-center text-gray-500 font-[Archivo]">
                        Loading behaviour analysis...
                        </div>
                    ) : (
                        <div className="w-full h-[35rem]"> 
                        <BehaviourScoreChart
                            chartData={allSubjectsBehaviourData.chartData}
                            subjects={allSubjectsBehaviourData.subjects}
                            activeSubject={active_subject_from_url}
                            analysisRange={allSubjectsBehaviourData.analysisRange}
                        />
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default SubjectAnalyticsBody;